import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { Teacher, User, Student, isTeacher } from '../../../utils/firebase';
import { doc, updateDoc, getDoc, setDoc, collection, getDocs, deleteDoc, query, where, Timestamp, writeBatch } from 'firebase/firestore';
import { db } from '../../../utils/firebase';
import Button from '../../../components/ui/Button';
import InputField from '../../../components/ui/InputField';
import ToggleSwitch from '../../../components/ui/ToggleSwitch';
import AddNewRoleDialog from '../../../components/ui/AddNewRoleDialog';
import EditRoleDialog from '../../../components/ui/EditRoleDialog';
import StudentRoleInfo from '../../../components/ui/StudentRoleInfo';
import SettingIcon from '../../../components/icons/SettingIcon';
import PlusCircleIcon from '../../../components/icons/PlusCircleIcon';
import TrashIcon from '../../../components/icons/TrashIcon';
import CheckCircleIcon from '../../../components/icons/CheckCircleIcon';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { printClassRoleRoster } from '../../../utils/printClassRoleRoster';

/** ClassManagement.handleRoleChange와 동일: 급여 주기별 다음 지급 시각 */
const getPointApprovalTargetDate = (salaryPeriod: 'daily' | 'weekly' | 'monthly'): Date => {
  const now = new Date();
  let targetDate = new Date(now);
  if (salaryPeriod === 'daily') {
    targetDate.setHours(23, 59, 0, 0);
  } else if (salaryPeriod === 'weekly') {
    const daysUntilSunday = 7 - now.getDay();
    targetDate.setDate(now.getDate() + daysUntilSunday);
    targetDate.setHours(23, 59, 0, 0);
  } else {
    targetDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    targetDate.setHours(23, 59, 0, 0);
  }
  return targetDate;
};

interface Role {
  id: string;
  name: string;
  description: string;
  salary: number;
  salaryType: 'point' | 'stamp';
  salaryPeriod: 'daily' | 'weekly' | 'monthly';
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
  isPointManager: boolean;
  pointAutoPay: boolean;
  maxCount: number;
  isAuctionable: boolean;
  docId?: string;
}

interface StudentRoleAssignment {
  studentId: string;
  studentName: string;
  studentNumber: number;
  roleId: string;
  roleName: string;
  position: string; // '부장' | '조원'
  assignedAt: Date;
  // assignedRoles의 isActive를 프론트에서 "만료 여부"로 활용
  isActive: boolean;
  salary: number;
  salaryType: 'point' | 'stamp';
  salaryPeriod: 'daily' | 'weekly' | 'monthly';
  pointAutoPay: boolean;
}

/** 역할 명단 테이블/인쇄용 (미배정 행은 roleName·position이 '없음') */
interface ClassRoleRosterTableRow {
  rowKey: string;
  studentId: string;
  studentName: string;
  studentNumber: number;
  roleName: string;
  position: string;
}

const sortAssignmentsForRoster = (a: StudentRoleAssignment, b: StudentRoleAssignment) => {
  if (a.studentNumber !== b.studentNumber) return a.studentNumber - b.studentNumber;
  const nameCmp = a.roleName.localeCompare(b.roleName, 'ko');
  if (nameCmp !== 0) return nameCmp;
  if (a.position === '부장' && b.position !== '부장') return -1;
  if (a.position !== '부장' && b.position === '부장') return 1;
  return 0;
};

const ClassRoleManagement: React.FC = () => {
  const { currentUser } = useAuth();
  const teacher = currentUser as Teacher;
  
  const [roles, setRoles] = useState<Role[]>([]);
  const [students, setStudents] = useState<Student[]>([]);
  const [studentRoleAssignments, setStudentRoleAssignments] = useState<StudentRoleAssignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedRoleFilter, setSelectedRoleFilter] = useState<string>('all');
  const [activeRoles, setActiveRoles] = useState<Record<string, boolean>>({});
  
  // 새 역할 생성 다이얼로그
  const [openCreateRoleDialog, setOpenCreateRoleDialog] = useState(false);
  
  // 역할 편집 다이얼로그
  const [openEditRoleDialog, setOpenEditRoleDialog] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  
  // 학생 추가 다이얼로그
  const [openAddStudentDialog, setOpenAddStudentDialog] = useState(false);
  const [selectedRoleForStudent, setSelectedRoleForStudent] = useState<string>('');
  const [selectedStudentId, setSelectedStudentId] = useState<string>('');
  const [selectedPosition, setSelectedPosition] = useState<string>('조원');
  
  // 학생 역할 정보 다이얼로그
  const [openStudentRoleInfoDialog, setOpenStudentRoleInfoDialog] = useState(false);
  const [selectedStudentRole, setSelectedStudentRole] = useState<StudentRoleAssignment | null>(null);

  // 다이얼로그가 열릴 때 body 스크롤 방지
  useEffect(() => {
    if (openAddStudentDialog) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [openAddStudentDialog]);

  // 역할 목록 및 학생 역할 할당 정보 가져오기
  useEffect(() => {
    const loadData = async () => {
      if (!currentUser || !isTeacher(currentUser)) return;

      try {
        setLoading(true);
        const { classId, schoolId } = currentUser;
        if (!classId || !schoolId) {
          setError('필수 정보가 누락되었습니다.');
          return;
        }

        // 역할 목록 가져오기
        const rolesRef = collection(db, `schools/${schoolId}/classes/${classId}/classRoles`);
        const rolesSnapshot = await getDocs(rolesRef);
        
        const rolesData = rolesSnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: data.id || doc.id,
            docId: doc.id,
            name: data.name || '',
            description: data.description || '',
            salary: data.salary || 0,
            salaryType: (data.salaryType || 'point') as 'point' | 'stamp',
            salaryPeriod: (data.salaryPeriod || 'monthly') as 'daily' | 'weekly' | 'monthly',
            isActive: data.isActive !== false,
            pointAutoPay: data.pointAutoPay || false,
            maxCount: data.maxCount || 1,
            isAuctionable: data.isAuctionable || false,
            isPointManager: data.isPointManager || false,
            createdAt: data.createdAt?.toDate() || new Date(),
            updatedAt: data.updatedAt?.toDate() || new Date()
          } as Role & { docId: string };
        });
        
        setRoles(rolesData);
        
        // 활성 상태 초기화
        const activeStates: Record<string, boolean> = {};
        rolesData.forEach(role => {
          activeStates[role.id] = role.isActive;
        });
        setActiveRoles(activeStates);

        // 학생 목록 가져오기
        const studentsRef = collection(db, `schools/${schoolId}/classes/${classId}/students`);
        const studentsSnapshot = await getDocs(studentsRef);
        const studentsData = studentsSnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            uid: doc.id,
            ...data,
            createdAt: data.createdAt?.toDate(),
            updatedAt: data.updatedAt?.toDate()
          } as Student;
        });
        setStudents(studentsData.sort((a, b) => (a.number || 0) - (b.number || 0)));

        // 학생 역할 할당 정보 가져오기
        const assignments: StudentRoleAssignment[] = [];
        studentsData.forEach(student => {
          const assignedRoles = student.assignedRoles || [];
          assignedRoles.forEach((assignment: any) => {
            const role = rolesData.find(r => r.id === assignment.roleId);
    if (role) {
              assignments.push({
                studentId: student.uid,
                studentName: student.name,
                studentNumber: student.number || 0,
                roleId: role.id,
                roleName: role.name,
                position: assignment.position || '조원',
                assignedAt: assignment.assignedAt?.toDate() || new Date(),
                isActive: assignment.isActive !== false,
        salary: role.salary,
        salaryType: role.salaryType,
        salaryPeriod: role.salaryPeriod,
                // assignedRoles에 pointAutoPay가 null/undefined로 들어오는 케이스를 대비
                pointAutoPay: assignment.pointAutoPay == null ? role.pointAutoPay : Boolean(assignment.pointAutoPay)
      });
            }
          });
        });
        setStudentRoleAssignments(assignments);
        
      } catch (err) {
        console.error('Error loading data:', err);
        setError('데이터를 불러오는 중 오류가 발생했습니다.');
      } finally {
        setLoading(false);
    }
  };

    loadData();
  }, [currentUser]);

  // 역할 활성화/비활성화 토글
  const handleToggleRole = async (roleId: string) => {
    if (!currentUser || !isTeacher(currentUser)) return;
    const { classId, schoolId } = currentUser;
    if (!classId || !schoolId) return;

    try {
      const role = roles.find(r => r.id === roleId);
      if (!role || !role.docId) return;

      const newActiveState = !activeRoles[roleId];
      const roleRef = doc(db, `schools/${schoolId}/classes/${classId}/classRoles`, role.docId);
      await updateDoc(roleRef, {
        isActive: newActiveState,
        updatedAt: new Date()
      });

      setActiveRoles(prev => ({ ...prev, [roleId]: newActiveState }));
      setRoles(prev => prev.map(r => r.id === roleId ? { ...r, isActive: newActiveState } : r));
      toast.success('역할 상태가 변경되었습니다.');
    } catch (err) {
      console.error('Error toggling role:', err);
      toast.error('역할 상태 변경 중 오류가 발생했습니다.');
    }
  };

  // 새 역할 생성
  const handleCreateRole = async (data: {
    name: string;
    description: string;
    salary: number;
    salaryType: 'point' | 'stamp';
    salaryPeriod: 'daily' | 'weekly' | 'monthly';
    maxCount: number;
    isPointManager: boolean;
    pointAutoPay: boolean;
  }) => {
    if (!currentUser || !isTeacher(currentUser)) return;
    const { classId, schoolId } = currentUser;
    if (!classId || !schoolId) return;

    if (!data.name.trim()) {
      toast.error('역할 이름을 입력해주세요.');
        return;
      }

    try {
      const roleRef = doc(collection(db, `schools/${schoolId}/classes/${classId}/classRoles`));
      const newRole: Omit<Role, 'id' | 'docId'> = {
        name: data.name,
        description: data.description,
        salary: data.salary,
        salaryType: data.salaryType,
        salaryPeriod: data.salaryPeriod,
        isActive: true,
        pointAutoPay: data.pointAutoPay,
        maxCount: data.maxCount,
        isAuctionable: false,
        isPointManager: data.isPointManager,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      const roleId = roleRef.id;
      await setDoc(roleRef, {
        ...newRole,
        id: roleId
      });

      const createdRole: Role = {
        id: roleId,
        docId: roleId,
        name: newRole.name,
        description: newRole.description,
        salary: newRole.salary,
        salaryType: newRole.salaryType,
        salaryPeriod: newRole.salaryPeriod,
        isActive: newRole.isActive,
        pointAutoPay: newRole.pointAutoPay,
        maxCount: newRole.maxCount,
        isAuctionable: newRole.isAuctionable,
        isPointManager: newRole.isPointManager,
        createdAt: newRole.createdAt,
        updatedAt: newRole.updatedAt
      };
      setRoles(prev => [...prev, createdRole]);
      setActiveRoles(prev => ({ ...prev, [roleId]: true }));

      toast.success('새 역할이 생성되었습니다.');
      setOpenCreateRoleDialog(false);
    } catch (err) {
      console.error('Error creating role:', err);
      toast.error('역할 생성 중 오류가 발생했습니다.');
    }
  };

  // 학생 역할 추가
  const handleAddStudentToRole = async () => {
    if (!currentUser || !isTeacher(currentUser)) return;
    const { classId, schoolId } = currentUser;
    if (!classId || !schoolId) return;

    if (!selectedRoleForStudent || !selectedStudentId) {
      toast.error('역할과 학생을 선택해주세요.');
        return;
      }

    try {
      const studentRef = doc(db, `schools/${schoolId}/classes/${classId}/students`, selectedStudentId);
      const studentDoc = await getDoc(studentRef);
      
      if (!studentDoc.exists()) {
        toast.error('학생 정보를 찾을 수 없습니다.');
        return;
      }

      const studentData = studentDoc.data();
      const currentAssignments = studentData.assignedRoles || [];

      // 이미 해당 역할이 할당되어 있는지 확인
      if (currentAssignments.some((a: any) => a.roleId === selectedRoleForStudent)) {
        toast.error('이미 해당 역할이 할당되어 있습니다.');
        return;
      }

      // 역할 정보 가져오기
      const role = roles.find(r => r.id === selectedRoleForStudent);
      if (!role) {
        toast.error('역할 정보를 찾을 수 없습니다.');
        return;
      }

      // 새로운 역할 할당 추가
      const newAssignment = {
        roleId: selectedRoleForStudent,
        position: selectedPosition,
        assignedAt: Timestamp.now(),
        isActive: true,
        pointAutoPay: role.pointAutoPay
      };

      await updateDoc(studentRef, {
        assignedRoles: [...currentAssignments, newAssignment]
      });

      const targetDate = getPointApprovalTargetDate(role.salaryPeriod);
      const approvalRef = doc(collection(db, `schools/${schoolId}/classes/${classId}/pointApprovals`));
      await setDoc(approvalRef, {
        uid: selectedStudentId,
        roleId: role.id,
        salaryType: role.salaryType,
        salaryPeriod: role.salaryPeriod,
        targetDate,
        isApproved: false,
        pointAutoPay: role.pointAutoPay,
        pointManagerApproved: null,
        teacherApproved: null,
        pointManagerApprovedAt: null,
        teacherApprovedAt: null,
        createdAt: new Date(),
        updatedAt: new Date(),
        schoolId,
        classId
      });

      const student = students.find(s => s.uid === selectedStudentId);
      if (student) {
        const newAssignmentLocal = {
          roleId: selectedRoleForStudent,
          position: selectedPosition,
          assignedAt: new Date(),
          isActive: true,
          pointAutoPay: role.pointAutoPay
        };
        setStudents(prev =>
          prev.map(s =>
            s.uid === selectedStudentId
              ? { ...s, assignedRoles: [...(s.assignedRoles || []), newAssignmentLocal as any] }
              : s
          )
        );
        setStudentRoleAssignments(prev => [
          ...prev,
          {
            studentId: selectedStudentId,
            studentName: student.name,
            studentNumber: student.number || 0,
            roleId: role.id,
            roleName: role.name,
            position: selectedPosition,
            assignedAt: newAssignmentLocal.assignedAt,
            isActive: true,
            salary: role.salary,
            salaryType: role.salaryType,
            salaryPeriod: role.salaryPeriod,
            pointAutoPay: role.pointAutoPay
          }
        ]);
      }

      toast.success('학생 역할이 추가되었습니다.');
      setOpenAddStudentDialog(false);
      setSelectedRoleForStudent('');
      setSelectedStudentId('');
      setSelectedPosition('조원');
    } catch (err) {
      console.error('Error adding student role:', err);
      toast.error('학생 역할 추가 중 오류가 발생했습니다.');
    }
  };

  // 역할 수정
  const handleUpdateRole = async (data: {
    name: string;
    description: string;
    salary: number;
    salaryType: 'point' | 'stamp';
    salaryPeriod: 'daily' | 'weekly' | 'monthly';
    maxCount: number;
    isPointManager: boolean;
    pointAutoPay: boolean;
  }) => {
    if (!currentUser || !isTeacher(currentUser) || !editingRole) return;
    const { classId, schoolId } = currentUser;
    if (!classId || !schoolId || !editingRole.docId) return;

    try {
      const roleRef = doc(db, `schools/${schoolId}/classes/${classId}/classRoles`, editingRole.docId);
      await updateDoc(roleRef, {
        name: data.name,
        description: data.description,
        salary: data.salary,
        salaryType: data.salaryType,
        salaryPeriod: data.salaryPeriod,
        maxCount: data.maxCount,
        isPointManager: data.isPointManager,
        pointAutoPay: data.pointAutoPay,
        updatedAt: new Date()
      });

      // 역할의 pointAutoPay 변경 시, 이미 배정된 학생들도 assignedRoles[].pointAutoPay를 동기화
      if (editingRole.pointAutoPay !== data.pointAutoPay) {
        const studentsRef = collection(db, `schools/${schoolId}/classes/${classId}/students`);
        const studentsSnapshot = await getDocs(studentsRef);

        for (const studentDoc of studentsSnapshot.docs) {
          const studentData = studentDoc.data();
          const currentAssignments: any[] = studentData.assignedRoles || [];

          let changed = false;
          const updatedAssignments = currentAssignments.map((a) => {
            if (a.roleId !== editingRole.id) return a;
            if (a.pointAutoPay === data.pointAutoPay) return a;
            changed = true;
            return { ...a, pointAutoPay: data.pointAutoPay };
          });

          if (!changed) continue;

          await updateDoc(doc(db, `schools/${schoolId}/classes/${classId}/students`, studentDoc.id), {
            assignedRoles: updatedAssignments,
            updatedAt: new Date()
          });
        }
      }

      setRoles(prev =>
        prev.map(r =>
          r.id === editingRole.id
            ? {
                ...r,
                name: data.name,
                description: data.description,
                salary: data.salary,
                salaryType: data.salaryType,
                salaryPeriod: data.salaryPeriod,
                maxCount: data.maxCount,
                isPointManager: data.isPointManager,
                pointAutoPay: data.pointAutoPay,
                updatedAt: new Date()
              }
            : r
        )
      );
      setStudentRoleAssignments(prev =>
        prev.map(a =>
          a.roleId === editingRole.id
            ? {
                ...a,
                roleName: data.name,
                salary: data.salary,
                salaryType: data.salaryType,
                salaryPeriod: data.salaryPeriod,
                pointAutoPay: data.pointAutoPay
              }
            : a
        )
      );

      toast.success('역할이 수정되었습니다.');
      setOpenEditRoleDialog(false);
      setEditingRole(null);
    } catch (err) {
      console.error('Error updating role:', err);
      toast.error('역할 수정 중 오류가 발생했습니다.');
    }
  };

  // 역할 삭제
  const handleDeleteRole = async () => {
    if (!currentUser || !isTeacher(currentUser) || !editingRole) return;
    const { classId, schoolId } = currentUser;
    if (!classId || !schoolId || !editingRole.docId) return;

    try {
      const roleRef = doc(db, `schools/${schoolId}/classes/${classId}/classRoles`, editingRole.docId);
      await deleteDoc(roleRef);

      const deletedId = editingRole.id;
      setRoles(prev => prev.filter(r => r.id !== deletedId));
      setActiveRoles(prev => {
        const next = { ...prev };
        delete next[deletedId];
        return next;
      });
      setStudentRoleAssignments(prev => prev.filter(a => a.roleId !== deletedId));

      toast.success('역할이 삭제되었습니다.');
      setOpenEditRoleDialog(false);
      setEditingRole(null);
    } catch (err) {
      console.error('Error deleting role:', err);
      toast.error('역할 삭제 중 오류가 발생했습니다.');
    }
  };

  // 학생 역할 삭제
  const handleDeleteStudentRole = async (studentId: string, roleId: string) => {
    if (!currentUser || !isTeacher(currentUser)) return;
    const { classId, schoolId } = currentUser;
    if (!classId || !schoolId) return;

    if (!window.confirm('정말로 이 학생의 역할을 삭제하시겠습니까?')) return;

    try {
      const studentRef = doc(db, `schools/${schoolId}/classes/${classId}/students`, studentId);
      const studentDoc = await getDoc(studentRef);
      
      if (!studentDoc.exists()) {
        toast.error('학생 정보를 찾을 수 없습니다.');
        return;
      }

      const studentData = studentDoc.data();
      const currentAssignments = studentData.assignedRoles || [];
      const updatedAssignments = currentAssignments.filter((a: any) => a.roleId !== roleId);

      const approvalsRef = collection(db, `schools/${schoolId}/classes/${classId}/pointApprovals`);
      const approvalsQuery = query(approvalsRef, where('uid', '==', studentId), where('roleId', '==', roleId));
      const approvalsSnapshot = await getDocs(approvalsQuery);
      const batch = writeBatch(db);
      approvalsSnapshot.docs.forEach(d => batch.delete(d.ref));
      batch.update(studentRef, {
        assignedRoles: updatedAssignments
      });
      await batch.commit();

      setStudents(prev =>
        prev.map(s =>
          s.uid === studentId
            ? {
                ...s,
                assignedRoles: (s.assignedRoles || []).filter((a: any) => a.roleId !== roleId)
              }
            : s
        )
      );
      setStudentRoleAssignments(prev =>
        prev.filter(a => !(a.studentId === studentId && a.roleId === roleId))
      );
      const closeStudentRoleInfo =
        selectedStudentRole?.studentId === studentId && selectedStudentRole?.roleId === roleId;
      if (closeStudentRoleInfo) {
        setOpenStudentRoleInfoDialog(false);
        setSelectedStudentRole(null);
      }

      toast.success('학생 역할이 삭제되었습니다.');
    } catch (err) {
      console.error('Error deleting student role:', err);
      toast.error('학생 역할 삭제 중 오류가 발생했습니다.');
    }
  };

  // 학생의 역할 수행 "만료 처리" 토글(= assignedRoles[].isActive) 업데이트
  const handleToggleStudentRoleExpired = async (
    studentId: string,
    roleId: string,
    isExpired: boolean
  ) => {
    if (!currentUser || !isTeacher(currentUser)) return;
    const { classId, schoolId } = currentUser;
    if (!classId || !schoolId) return;

    try {
      const studentRef = doc(db, `schools/${schoolId}/classes/${classId}/students`, studentId);
      const studentDoc = await getDoc(studentRef);

      if (!studentDoc.exists()) {
        toast.error('학생 정보를 찾을 수 없습니다.');
        return;
      }

      const studentData = studentDoc.data();
      const currentAssignments = studentData.assignedRoles || [];
      const updatedAssignments = currentAssignments.map((a: any) => {
        if (a.roleId !== roleId) return a;
        return {
          ...a,
          isActive: !isExpired
        };
      });

      await updateDoc(studentRef, {
        assignedRoles: updatedAssignments,
        updatedAt: new Date()
      });

      // UI 즉시 반영
      setStudentRoleAssignments(prev =>
        prev.map(a =>
          a.studentId === studentId && a.roleId === roleId
            ? { ...a, isActive: !isExpired }
            : a
        )
      );
      setSelectedStudentRole(prev => {
        if (!prev) return prev;
        if (prev.studentId !== studentId || prev.roleId !== roleId) return prev;
        return { ...prev, isActive: !isExpired };
      });

      toast.success('만료 처리 상태가 변경되었습니다.');
    } catch (err) {
      console.error('Error toggling student role expired:', err);
      toast.error('만료 처리 상태 변경 중 오류가 발생했습니다.');
    }
  };

  // 필터링된 역할 목록
  const filteredRoles = selectedRoleFilter === 'all' 
    ? roles 
    : roles.filter(r => r.id === selectedRoleFilter);

  const rosterFilterLabel =
    selectedRoleFilter === 'all'
      ? '전체 역할'
      : roles.find(r => r.id === selectedRoleFilter)?.name ?? '선택 역할';

  const rosterRows = useMemo((): ClassRoleRosterTableRow[] => {
    const sortedStudents = [...students].sort((a, b) => (a.number || 0) - (b.number || 0));
    const rows: ClassRoleRosterTableRow[] = [];

    if (selectedRoleFilter === 'all') {
      for (const s of sortedStudents) {
        const ass = studentRoleAssignments
          .filter(a => a.studentId === s.uid)
          .sort(sortAssignmentsForRoster);
        if (ass.length === 0) {
          rows.push({
            rowKey: `${s.uid}-unassigned-all`,
            studentId: s.uid,
            studentName: s.name,
            studentNumber: s.number || 0,
            roleName: '없음',
            position: '없음'
          });
        } else {
          for (const a of ass) {
            rows.push({
              rowKey: `${a.studentId}-${a.roleId}`,
              studentId: a.studentId,
              studentName: a.studentName,
              studentNumber: a.studentNumber,
              roleName: a.roleName,
              position: a.position
            });
          }
        }
      }
      return rows;
    }

    for (const s of sortedStudents) {
      const ass = studentRoleAssignments
        .filter(a => a.studentId === s.uid && a.roleId === selectedRoleFilter)
        .sort(sortAssignmentsForRoster);
      if (ass.length === 0) {
        rows.push({
          rowKey: `${s.uid}-unassigned-${selectedRoleFilter}`,
          studentId: s.uid,
          studentName: s.name,
          studentNumber: s.number || 0,
          roleName: '없음',
          position: '없음'
        });
      } else {
        for (const a of ass) {
          rows.push({
            rowKey: `${a.studentId}-${a.roleId}`,
            studentId: a.studentId,
            studentName: a.studentName,
            studentNumber: a.studentNumber,
            roleName: a.roleName,
            position: a.position
          });
        }
      }
    }
    return rows;
  }, [students, studentRoleAssignments, selectedRoleFilter]);

  const handlePrintClassRoleRoster = () => {
    printClassRoleRoster({
      teacher,
      filterLabel: rosterFilterLabel,
      rows: rosterRows.map(r => ({
        studentNumber: r.studentNumber,
        studentName: r.studentName,
        roleName: r.roleName,
        position: r.position
      }))
    });
  };

  // 역할별 학생 목록
  const getStudentsByRole = (roleId: string) => {
    return studentRoleAssignments
      .filter(a => a.roleId === roleId)
      .sort((a, b) => {
        // 부장 우선, 그 다음 번호순
        if (a.position === '부장' && b.position !== '부장') return -1;
        if (a.position !== '부장' && b.position === '부장') return 1;
        return a.studentNumber - b.studentNumber;
      });
  };

  // 역할별 통계
  const getRoleStats = (roleId: string) => {
    const role = roles.find(r => r.id === roleId);
    if (!role) {
      return { 
        total: 0, 
        positions: {},
        maxCount: 0,
        availablePositions: {
          부장: 0,
          조원: 0
        }
      };
    }
    
    const roleStudents = getStudentsByRole(roleId);
    const positions: Record<string, number> = {};
    roleStudents.forEach(s => {
      positions[s.position] = (positions[s.position] || 0) + 1;
    });

          return {
      total: roleStudents.length,
      positions,
      maxCount: role.maxCount,
      availablePositions: {
        부장: (role.maxCount > 0 ? 1 : 0) - (positions['부장'] || 0),
        조원: (role.maxCount > 0 ? role.maxCount - 1 : 0) - (positions['조원'] || 0)
      }
    };
  };

  // 역할 수행 기간 계산
  const getRoleDuration = (assignedAt: Date) => {
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - assignedAt.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // 급여 지급 주기 텍스트
  const getSalaryPeriodText = (period: 'daily' | 'weekly' | 'monthly') => {
    switch (period) {
      case 'daily':
        return '매일 지급';
      case 'weekly':
        return '매주 금요일 지급';
      case 'monthly':
        return '매월 1일 지급';
      default:
        return '';
    }
  };

  // 급여 타입 텍스트
  const getSalaryTypeText = (type: 'point' | 'stamp') => {
    return type === 'point' ? '포인트' : '도장';
  };

  const studentsAvailableForSelectedRole = students.filter(s =>
    !studentRoleAssignments.some(
      a => a.studentId === s.uid && a.roleId === selectedRoleForStudent
    )
  );

  if (loading) {
  return (
      <div style={{ 
        width: '100%', 
        height: '100vh', 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        background: '#F4F6FA'
      }}>
        <div style={{ color: '#161616', fontSize: '16px' }}>로딩 중...</div>
      </div>
    );
  }

  return (
    <div style={{
      width: '100%',
      minHeight: 'fit-content',
      display: 'flex',
      flexDirection: 'column',
      gap: '20px',
      overflowY: 'auto',
      overflowX: 'hidden'
    }}>
        {/* 헤더 */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '8px'
        }}>
          <div style={{
            color: '#161616',
            fontSize: '16px',
            fontFamily: 'Pretendard',
            fontWeight: '400',
            lineHeight: '24px'
          }}>
            {teacher?.homeroomClass ? `${teacher.homeroomClass.grade}학년 ${teacher.homeroomClass.class}반` : '학급 정보 없음'}
          </div>
          <div style={{
            color: '#161616',
            fontSize: '32px',
            fontFamily: 'Pretendard',
            fontWeight: '600',
            lineHeight: '48px'
          }}>
            학급 역할
          </div>
        </div>

        {/* 역할 필터 및 새 역할 생성 버튼 */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: '20px'
        }}>
          {/* 역할 필터 */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px'
          }}>
            <div style={{
              color: '#161616',
              fontSize: '18px',
              fontFamily: 'Pretendard',
              fontWeight: '600',
              lineHeight: '27px'
            }}>
              역할 목록
            </div>
            <div style={{
              display: 'flex',
              gap: '20px',
              flexWrap: 'wrap'
            }}>
              <div
                onClick={() => setSelectedRoleFilter('all')}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '2px',
                  cursor: 'pointer',
                  padding: '4px 8px'
                }}
              >
                {selectedRoleFilter === 'all' ? (
                  <CheckCircleIcon width={14} height={13} color="#FF6F0F" />
                ) : (
                  <div style={{
                    width: '14px',
                    height: '13px',
                    borderRadius: '50%',
                    background: '#BABABA'
                  }} />
                )}
                <div style={{
                  color: selectedRoleFilter === 'all' ? '#161616' : '#989898',
                  fontSize: '12px',
                  fontFamily: 'Pretendard',
                  fontWeight: '400',
                  lineHeight: '18px'
                }}>
                  전체
                </div>
              </div>
              {roles.map(role => (
                <div
                  key={role.id}
                  onClick={() => setSelectedRoleFilter(role.id)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '2px',
                    cursor: 'pointer',
                    padding: '4px 8px'
                  }}
                >
                  {selectedRoleFilter === role.id ? (
                    <CheckCircleIcon width={14} height={13} color="#FF6F0F" />
                  ) : (
                    <div style={{
                      width: '14px',
                      height: '13px',
                      borderRadius: '50%',
                      background: '#BABABA'
                    }} />
                  )}
                  <div style={{
                    color: selectedRoleFilter === role.id ? '#161616' : '#989898',
                    fontSize: '12px',
                    fontFamily: 'Pretendard',
                    fontWeight: '400',
                    lineHeight: '18px'
                  }}>
                    {role.name}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* 새 역할 생성 버튼 */}
            <Button
            variant="submit"
            onClick={() => setOpenCreateRoleDialog(true)}
            leftIcon={<PlusCircleIcon width={16} height={16} color="white" />}
            width="118px"
            height="40px"
            >
              새 역할 생성
            </Button>
        </div>

        {/* 역할 명단 (화면 + 인쇄) */}
        <div
          style={{
            width: '100%',
            background: 'var(--Color-background-normal, white)',
            borderRadius: '16px',
            padding: '20px',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            border: '1px solid var(--Color-line-normal, #F0F0F0)'
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '12px'
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <div
                style={{
                  color: 'var(--Color-text-strong, #161616)',
                  fontSize: '18px',
                  fontFamily: 'Pretendard',
                  fontWeight: '600',
                  lineHeight: '27px'
                }}
              >
                역할 명단
              </div>
              <div
                style={{
                  color: 'var(--Color-text-normal, #989898)',
                  fontSize: '13px',
                  fontFamily: 'Pretendard',
                  fontWeight: '400',
                  lineHeight: '19.5px'
                }}
              >
                {rosterFilterLabel} · 학생 {students.length}명 · 명단 {rosterRows.length}행
              </div>
            </div>
            <Button
              variant="secondary"
              onClick={handlePrintClassRoleRoster}
              height="40px"
              disabled={students.length === 0 || rosterRows.length === 0}
            >
              명단 출력
            </Button>
          </div>

          {students.length === 0 ? (
            <div
              style={{
                color: 'var(--Color-text-normal, #989898)',
                fontSize: '14px',
                padding: '8px 0'
              }}
            >
              등록된 학생이 없습니다.
            </div>
          ) : rosterRows.length === 0 ? (
            <div
              style={{
                color: 'var(--Color-text-normal, #989898)',
                fontSize: '14px',
                padding: '8px 0'
              }}
            >
              표시할 명단이 없습니다.
            </div>
          ) : (
            <div style={{ overflowX: 'auto', width: '100%' }}>
              <table
                style={{
                  width: '100%',
                  minWidth: '520px',
                  borderCollapse: 'collapse',
                  fontSize: '14px',
                  fontFamily: 'Pretendard'
                }}
              >
                <thead>
                  <tr>
                    {['No', '번호', '이름', '역할', '직급'].map((label) => (
                      <th
                        key={label}
                        style={{
                          textAlign: 'left',
                          padding: '10px 12px',
                          borderBottom: '1px solid #E9E9E9',
                          color: '#989898',
                          fontWeight: 500,
                          fontSize: '13px',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rosterRows.map((a, index) => (
                    <tr key={a.rowKey}>
                      <td
                        style={{
                          padding: '12px',
                          borderBottom: '1px solid #F0F0F0',
                          color: '#161616'
                        }}
                      >
                        {index + 1}
                      </td>
                      <td
                        style={{
                          padding: '12px',
                          borderBottom: '1px solid #F0F0F0',
                          color: '#161616'
                        }}
                      >
                        {a.studentNumber}번
                      </td>
                      <td
                        style={{
                          padding: '12px',
                          borderBottom: '1px solid #F0F0F0',
                          color: '#161616'
                        }}
                      >
                        {a.studentName}
                      </td>
                      <td
                        style={{
                          padding: '12px',
                          borderBottom: '1px solid #F0F0F0',
                          color: a.roleName === '없음' ? '#989898' : '#161616'
                        }}
                      >
                        {a.roleName}
                      </td>
                      <td
                        style={{
                          padding: '12px',
                          borderBottom: '1px solid #F0F0F0',
                          color: a.roleName === '없음' ? '#989898' : '#161616'
                        }}
                      >
                        {a.position}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* 역할별 카드 섹션 */}
        {filteredRoles.map(role => {
          const roleStudents = getStudentsByRole(role.id);
          const stats = getRoleStats(role.id);
          const isActive = activeRoles[role.id] !== false;

          return (
            <div
              key={role.id}
              style={{
                width: '100%',
                background: 'white',
                borderRadius: '16px',
                padding: '20px',
                display: 'flex',
                flexDirection: 'column',
                gap: '16px'
              }}
            >
              {/* 역할 헤더 */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: '12px'
              }}>
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '4px',
                  flex: 1
                }}>
                  <div style={{
                    display: 'flex',
                alignItems: 'center',
                    gap: '8px'
                  }}>
                    <div style={{
                      color: isActive ? '#161616' : '#989898',
                      fontSize: '16px',
                      fontFamily: 'Pretendard',
                      fontWeight: '600',
                      lineHeight: '24px'
                    }}>
                      {role.name}
                    </div>
                    <div style={{
                      color: isActive ? '#FF6F0F' : '#BABABA',
                      fontSize: '16px',
                      fontFamily: 'Pretendard',
                      fontWeight: '500',
                      lineHeight: '24px'
                    }}>
                      {stats.total}명
                    </div>
                  </div>
                  <div style={{
                    color: isActive ? '#989898' : '#BABABA',
                    fontSize: '12px',
                    fontFamily: 'Pretendard',
                    fontWeight: '400',
                    lineHeight: '18px'
                  }}>
                    부여 가능 인원 {role.maxCount}명 | 부장({stats.availablePositions?.부장 ?? 0}), 조원({stats.availablePositions?.조원 ?? 0})
                  </div>
                </div>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <div
                    onClick={() => {
                      setEditingRole(role);
                      setOpenEditRoleDialog(true);
                    }}
                    style={{
                      padding: '2px',
                      cursor: 'pointer'
                    }}
                  >
                    <SettingIcon width={16} height={16} color="#989898" />
                  </div>
                  <ToggleSwitch
                    checked={isActive}
                    onChange={() => handleToggleRole(role.id)}
                    size="medium"
                  />
                </div>
              </div>

              {/* 학생 목록 테이블 */}
              {isActive && (
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px'
                }}>
                  {/* 테이블 헤더 */}
                  <div style={{
                    display: 'flex',
                    borderBottom: '1px solid #E9E9E9',
                    paddingBottom: '12px'
                  }}>
                    <div style={{ width: '103px', paddingLeft: '16px', paddingRight: '16px' }}>
                      <div style={{
                        color: '#989898',
                        fontSize: '13px',
                        fontFamily: 'Pretendard',
                        fontWeight: '500',
                        lineHeight: '19.5px'
                      }}>
                        No
                      </div>
                    </div>
                    <div style={{ width: '102px', paddingLeft: '8px', paddingRight: '8px' }}>
                      <div style={{
                        color: '#989898',
                        fontSize: '13px',
                        fontFamily: 'Pretendard',
                        fontWeight: '500',
                        lineHeight: '19.5px'
                      }}>
                        직급
                      </div>
                    </div>
                    <div style={{ width: '102px', paddingLeft: '8px', paddingRight: '8px' }}>
                      <div style={{
                        color: '#989898',
                        fontSize: '13px',
                        fontFamily: 'Pretendard',
                        fontWeight: '500',
                        lineHeight: '19.5px'
                      }}>
                        이름
                      </div>
                    </div>
                    <div style={{ width: '151px', paddingLeft: '8px', paddingRight: '8px' }}>
                      <div style={{
                        color: '#989898',
                        fontSize: '13px',
                        fontFamily: 'Pretendard',
                        fontWeight: '500',
                        lineHeight: '19.5px'
                      }}>
                        번호
                      </div>
                    </div>
                    <div style={{ width: '236px', paddingLeft: '8px', paddingRight: '8px' }}>
                      <div style={{
                        color: '#989898',
                        fontSize: '13px',
                        fontFamily: 'Pretendard',
                        fontWeight: '500',
                        lineHeight: '19.5px'
                      }}>
                        역할 수행기간
                      </div>
                    </div>
                    <div style={{ width: '213px', paddingLeft: '8px', paddingRight: '8px' }}>
                      <div style={{
                        color: '#989898',
                        fontSize: '13px',
                        fontFamily: 'Pretendard',
                        fontWeight: '500',
                        lineHeight: '19.5px'
                      }}>
                        급여
                      </div>
                    </div>
                    <div style={{ width: '202px', paddingLeft: '8px', paddingRight: '8px' }}>
                      <div style={{
                        color: '#989898',
                        fontSize: '13px',
                        fontFamily: 'Pretendard',
                        fontWeight: '500',
                        lineHeight: '19.5px'
                      }}>
                        포인트 자동 지급 여부
                      </div>
                    </div>
                    <div style={{ width: '65px', paddingLeft: '8px', paddingRight: '8px' }}>
                      <div style={{
                        color: '#989898',
                        fontSize: '13px',
                        fontFamily: 'Pretendard',
                        fontWeight: '500',
                        lineHeight: '19.5px'
                      }}>
                        관리
                      </div>
                    </div>
                  </div>

                  {/* 테이블 행 */}
                  {roleStudents.map((assignment, index) => {
                    const duration = getRoleDuration(assignment.assignedAt);
                    const salaryText = getSalaryTypeText(assignment.salaryType);
                    const periodText = getSalaryPeriodText(assignment.salaryPeriod);
                    
                    return (
                      <div
                        key={`${assignment.studentId}-${assignment.roleId}`}
                        style={{
                          display: 'flex',
                          borderBottom: '1px solid #F0F0F0',
                          paddingTop: '12px',
                          paddingBottom: '12px'
                        }}
                      >
                        <div style={{ width: '103px', paddingLeft: '16px', paddingRight: '16px' }}>
                          <div style={{
                            color: '#161616',
                            fontSize: '14px',
                            fontFamily: 'Pretendard',
                            fontWeight: '400',
                            lineHeight: '21px'
                          }}>
                            {index + 1}
                          </div>
                        </div>
                        <div style={{ width: '102px', paddingLeft: '8px', paddingRight: '8px' }}>
                          <div style={{
                            padding: '2px 12px',
                            background: assignment.position === '부장' ? '#293044' : '#707482',
                            borderRadius: '8px',
                            display: 'inline-block'
                          }}>
                            <div style={{
                              color: 'white',
                              fontSize: '12px',
                              fontFamily: 'Pretendard',
                              fontWeight: '500',
                              lineHeight: '18px'
                            }}>
                              {assignment.position}
                            </div>
                          </div>
                        </div>
                        <div style={{ width: '102px', paddingLeft: '8px', paddingRight: '8px' }}>
                          <div style={{
                            color: '#161616',
                            fontSize: '14px',
                            fontFamily: 'Pretendard',
                            fontWeight: '400',
                            lineHeight: '21px'
                          }}>
                            {assignment.studentName}
                          </div>
                        </div>
                        <div style={{ width: '151px', paddingLeft: '8px', paddingRight: '8px' }}>
                          <div style={{
                            color: '#161616',
                            fontSize: '14px',
                            fontFamily: 'Pretendard',
                            fontWeight: '400',
                            lineHeight: '21px'
                          }}>
                            {assignment.studentNumber}번
                          </div>
                        </div>
                        <div style={{ width: '236px', paddingLeft: '8px', paddingRight: '8px' }}>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}>
                            <div style={{
                              color: '#161616',
                              fontSize: '14px',
                              fontFamily: 'Pretendard',
                              fontWeight: '400',
                              lineHeight: '21px'
                            }}>
                              {duration}일
                            </div>
                            <div style={{
                              color: '#989898',
                              fontSize: '14px',
                              fontFamily: 'Pretendard',
                              fontWeight: '400',
                              lineHeight: '21px'
                            }}>
                              ({format(assignment.assignedAt, 'yyyy.MM.dd')}~)
                            </div>
                          </div>
                        </div>
                        <div style={{ width: '213px', paddingLeft: '8px', paddingRight: '8px' }}>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}>
                            <div style={{
                              padding: '2px 12px',
                              background: '#FFD2B5',
                              borderRadius: '8px',
                              display: 'inline-block'
                            }}>
                              <div style={{
                                color: '#FF6F0F',
                                fontSize: '12px',
                                fontFamily: 'Pretendard',
                                fontWeight: '500',
                                lineHeight: '18px'
                              }}>
                                {salaryText === '포인트' 
                                  ? (assignment.salaryPeriod === 'daily' ? '일급' : assignment.salaryPeriod === 'weekly' ? '주급' : '월급')
                                  : salaryText}
                              </div>
                            </div>
                            <div style={{
                              color: '#161616',
                              fontSize: '14px',
                              fontFamily: 'Pretendard',
                              fontWeight: '400',
                              lineHeight: '21px'
                            }}>
                              {assignment.salary} {salaryText}
                            </div>
                          </div>
                        </div>
                        <div style={{ width: '202px', paddingLeft: '8px', paddingRight: '8px' }}>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}>
                            <div style={{
                              padding: '2px 12px',
                              background: assignment.pointAutoPay ? '#F5F5F5' : '#F8F8F8',
                              borderRadius: '8px',
                              display: 'inline-block'
                            }}>
                              <div style={{
                                color: '#33AFD7',
                                fontSize: '12px',
                                fontFamily: 'Pretendard',
                                fontWeight: '500',
                                lineHeight: '18px'
                              }}>
                                {assignment.pointAutoPay ? '자동' : '수동'}
                              </div>
                            </div>
                            {assignment.pointAutoPay && (
                              <div style={{
                                color: '#161616',
                                fontSize: '14px',
                                fontFamily: 'Pretendard',
                                fontWeight: '400',
                                lineHeight: '21px'
                              }}>
                                {periodText}
                              </div>
                            )}
                          </div>
                        </div>
                        <div style={{ 
                          width: '65px', 
                          paddingLeft: '8px', 
                          paddingRight: '8px',
                          display: 'flex',
                          gap: '9px'
                        }}>
                          <div
                  onClick={() => {
                              setSelectedStudentRole(assignment);
                              setOpenStudentRoleInfoDialog(true);
                            }}
                            style={{
                              width: '20px',
                              height: '20px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            <SettingIcon width={16} height={16} color="#989898" />
                          </div>
                          <div
                            onClick={() => handleDeleteStudentRole(assignment.studentId, assignment.roleId)}
                            style={{
                              width: '20px',
                              height: '20px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                >
                            <TrashIcon width={16} height={16} color="#989898" />
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  {/* 학생 추가하기 버튼 */}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    padding: '10px 574px',
                    background: '#FDFDFD',
                    borderRadius: '8px',
                    border: '1px solid #F0F0F0',
                    marginTop: '8px'
                  }}>
                    <Button
                      variant="line"
                      onClick={() => {
                        setSelectedRoleForStudent(role.id);
                        setOpenAddStudentDialog(true);
                      }}
                      leftIcon={
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M6 2V10M2 6H10" stroke="#161616" strokeWidth="1.2" strokeLinecap="round"/>
                        </svg>
                      }
                      width="90px"
                      height="25px"
                  size="small"
                    >
                      학생 추가하기
                    </Button>
                  </div>
                </div>
              )}
            </div>
          );
        })}

      {/* 새 역할 생성 다이얼로그 */}
      <AddNewRoleDialog
        isOpen={openCreateRoleDialog}
        onClose={() => setOpenCreateRoleDialog(false)}
        onSubmit={handleCreateRole}
      />

      {/* 역할 편집 다이얼로그 */}
      <EditRoleDialog
        isOpen={openEditRoleDialog}
        onClose={() => {
          setOpenEditRoleDialog(false);
          setEditingRole(null);
        }}
        onUpdate={handleUpdateRole}
        onDelete={handleDeleteRole}
        initialData={editingRole ? {
          name: editingRole.name,
          description: editingRole.description,
          salary: editingRole.salary,
          salaryType: editingRole.salaryType,
          salaryPeriod: editingRole.salaryPeriod,
          maxCount: editingRole.maxCount,
          isPointManager: editingRole.isPointManager,
          pointAutoPay: editingRole.pointAutoPay
        } : undefined}
      />

      {/* 학생 역할 정보 다이얼로그 */}
      {selectedStudentRole && (
        <StudentRoleInfo
          isOpen={openStudentRoleInfoDialog}
          onClose={() => {
            setOpenStudentRoleInfoDialog(false);
            setSelectedStudentRole(null);
          }}
          onComplete={() => {
            setOpenStudentRoleInfoDialog(false);
            setSelectedStudentRole(null);
          }}
          studentName={selectedStudentRole.studentName}
          studentNumber={selectedStudentRole.studentNumber}
          roleName={selectedStudentRole.roleName}
          position={selectedStudentRole.position}
          assignedAt={selectedStudentRole.assignedAt}
          salary={selectedStudentRole.salary}
          salaryType={selectedStudentRole.salaryType}
          salaryPeriod={selectedStudentRole.salaryPeriod}
          pointAutoPay={selectedStudentRole.pointAutoPay}
          isExpired={!selectedStudentRole.isActive}
          onExpireToggle={(checked) =>
            handleToggleStudentRoleExpired(
              selectedStudentRole.studentId,
              selectedStudentRole.roleId,
              checked
            )
          }
        />
      )}

      {/* 학생 추가 다이얼로그 */}
      {openAddStudentDialog && createPortal(
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(0, 0, 0, 0.5)',
            zIndex: 99999,
            overflow: 'hidden',
            pointerEvents: 'auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
          onClick={(e) => {
            // 배경 클릭 시 닫기
            if (e.target === e.currentTarget) {
              setOpenAddStudentDialog(false);
              setSelectedRoleForStudent('');
              setSelectedStudentId('');
              setSelectedPosition('조원');
            }
          }}
        >
          <div 
            style={{
              background: 'white',
              borderRadius: '16px',
              padding: '24px 28px',
              width: '500px',
              maxWidth: 'calc(100vw - 40px)',
              maxHeight: 'calc(100vh - 40px)',
              overflow: 'auto',
              boxShadow: '0px 8px 16px rgba(0, 0, 0, 0.12)',
              display: 'flex',
              flexDirection: 'column'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              fontSize: '20px',
              fontFamily: 'Pretendard',
              fontWeight: '600',
              lineHeight: '30px',
              color: '#161616',
              marginBottom: '8px'
            }}>
              학생 추가하기
            </div>
            <div style={{
              fontSize: '13px',
              lineHeight: '19.5px',
              color: '#989898',
              marginBottom: '16px',
              fontFamily: 'Pretendard'
            }}>
              역할에 넣을 학생과 직급을 선택하세요.
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div style={{ fontSize: '13px', fontWeight: 600, color: '#161616', fontFamily: 'Pretendard' }}>학생</div>
                <div
                  style={{
                    maxHeight: '240px',
                    overflowY: 'auto',
                    border: '1px solid #F0F0F0',
                    borderRadius: '8px'
                  }}
                >
                  {studentsAvailableForSelectedRole.length === 0 ? (
                    <div style={{ padding: '20px 16px', color: '#989898', fontSize: '13px', fontFamily: 'Pretendard' }}>
                      추가할 수 있는 학생이 없습니다.
                    </div>
                  ) : (
                    studentsAvailableForSelectedRole.map(s => {
                        const selected = selectedStudentId === s.uid;
                        return (
                          <button
                            key={s.uid}
                            type="button"
                            onClick={() => setSelectedStudentId(s.uid)}
                            style={{
                              width: '100%',
                              textAlign: 'left',
                              padding: '12px 16px',
                              border: 'none',
                              borderBottom: '1px solid #F0F0F0',
                              background: selected ? '#FFF4ED' : 'white',
                              color: selected ? '#FF6F0F' : '#161616',
                              fontSize: '14px',
                              fontWeight: selected ? 600 : 400,
                              fontFamily: 'Pretendard',
                              cursor: 'pointer'
                            }}
                          >
                            {s.name} ({s.number}번)
                          </button>
                        );
                      })
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div style={{ fontSize: '13px', fontWeight: 600, color: '#161616', fontFamily: 'Pretendard' }}>직급</div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  {(['부장', '조원'] as const).map((position) => {
                    const selected = selectedPosition === position;
                    return (
                      <button
                        key={position}
                        type="button"
                        onClick={() => setSelectedPosition(position)}
                        style={{
                          flex: 1,
                          height: '40px',
                          borderRadius: '8px',
                          border: selected ? '1.5px solid #FF6F0F' : '1px solid #F0F0F0',
                          background: selected ? '#FFF4ED' : 'white',
                          color: selected ? '#FF6F0F' : '#161616',
                          fontSize: '14px',
                          fontWeight: 600,
                          fontFamily: 'Pretendard',
                          cursor: 'pointer'
                        }}
                      >
                        {position}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px',
              marginTop: '24px'
            }}>
          <Button 
                variant="cancel"
            onClick={() => {
                  setOpenAddStudentDialog(false);
                  setSelectedRoleForStudent('');
                  setSelectedStudentId('');
                  setSelectedPosition('조원');
                }}
                width="80px"
                height="40px"
          >
                취소
          </Button>
              <Button
                variant="submit"
                onClick={handleAddStudentToRole}
                width="80px"
                height="40px"
              >
                추가
          </Button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default ClassRoleManagement; 